import { useState } from 'react'
import { SECTIONS, newId, type PortalConfig, type Promotion, type SpecialsSection as Doc } from '@sonder/shared'
import { useClientView } from '@/app/mode'
import { useSectionEditor } from '@/data/editor'
import { reviewInfo, useNameOf } from '@/data/review'
import { CopyToDialog, PromotionsBuilder, ReviewBar, ReviewTag, useToast } from '@/ui'
import { BlockHead, NoEntities, useLocations } from './common'
import { FieldEditor } from './FieldEditor'

const EVENTS = SECTIONS.specials.fields.find((f) => f.key === 'events')!

export function SpecialsSection({ clientId }: { clientId: string; config: PortalConfig }) {
  const ed = useSectionEditor<Doc>(clientId, 'specials')
  const locations = useLocations(clientId)
  const clientView = useClientView()
  const nameOf = useNameOf()
  const [copying, setCopying] = useState<{ from: string; promo: Promotion } | null>(null)
  const { toast } = useToast()
  if (!ed.doc) return null
  // Each location gets its own copy (a fresh id), so the details can still differ later.
  const copyPromo = (ids: string[]) => {
    if (!copying) return
    for (const t of ids) {
      const cur = ed.getField<Promotion[]>(`promotions.${t}`)?.value ?? []
      ed.setValue(`promotions.${t}`, [...cur, { ...structuredClone(copying.promo), id: newId().slice(0, 8) }])
    }
    const name = copying.promo.name.trim() || 'Promotion'
    toast(`Copied ${name} to ${ids.length === 1 ? locations.name(ids[0]!) : `${ids.length} locations`}`, 'success')
    setCopying(null)
  }

  return (
    <div className="grid gap-12">
      <CopyToDialog
        open={copying !== null}
        onClose={() => setCopying(null)}
        what={copying?.promo.name.trim() || 'this promotion'}
        targets={locations.order.filter((t) => t !== copying?.from).map((t) => ({ id: t, label: locations.name(t) }))}
        onCopy={copyPromo}
      />
      {locations.order.length === 0 && <NoEntities what="locations" hint="Specials are per location; add locations first." />}
      {locations.order.map((locId) => {
        const promos = ed.getField<Promotion[]>(`promotions.${locId}`)
        const review = clientView ? reviewInfo(promos, nameOf, { onConfirm: (v) => ed.confirm(`promotions.${locId}`, v), onUndo: () => ed.undo(`promotions.${locId}`) }) : undefined
        return (
          <section key={locId} data-field={`promotions.${locId}`} className="grid gap-6 border-t border-ink pt-6">
            <BlockHead eyebrow="Specials & promotions" title={locations.name(locId)} aside={review && <ReviewTag state={review.state} />} />
            <div className="max-w-form">
              <PromotionsBuilder
                value={promos?.value ?? []}
                onChange={(v) => ed.setValue(`promotions.${locId}`, v)}
                onCopy={locations.order.length > 1 ? (promo) => setCopying({ from: locId, promo }) : undefined}
              />
            </div>
            {review && <ReviewBar review={review} className="max-w-form" />}
          </section>
        )
      })}
      <section className="grid gap-6 border-t border-ink pt-6">
        <BlockHead eyebrow="Events & milestones" title={locations.order.length > 1 ? 'Across every location' : 'Coming up'} />
        <div className="max-w-form">
          <FieldEditor editor={ed} path="events" def={EVENTS} rows={4} />
        </div>
      </section>
    </div>
  )
}
